import { forwardRef, useState } from "react";
import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import Link from "next/link";

const SidebarItem = forwardRef(
  ({ item, isActive, isCollapsed, onClick }, ref) => {
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const iconRef = useRef();
    const hasChildren = !!item.children?.length;
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
      if (showTooltip && iconRef.current) {
        const rect = iconRef.current.getBoundingClientRect();
        setTooltipPosition({
          top: rect.top + rect.height / 2,
          left: rect.right + 20,
        });
      }
    }, [showTooltip]);

    // Show tooltip when sidebar is collapsed or on hover
    const shouldShowTooltip = showTooltip && isCollapsed;

    return (
      <div
        ref={ref}
        className={`my-2 ${
          isActive || showTooltip ? "rounded" : ""
        } position-relative`}
        style={{
          width: "100%",
          height: "40px",
          backgroundColor:
            isActive || showTooltip
              ? "rgba(255, 255, 255, 0.1)"
              : "transparent",
        }}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {hasChildren ? (
          <span
            className="d-flex align-items-center icon-wrapper w-100 h-100"
            ref={iconRef}
            onClick={onClick}
            style={{
              background:
                isActive || showTooltip ? "var(--bs-white)" : "transparent",
              border: "none",
              color:
                isActive || showTooltip
                  ? "var(--bs-bota-light-blue)"
                  : "var(--bs-white)",
              cursor: "pointer",
              paddingLeft: isCollapsed ? "0" : "16px",
              justifyContent: isCollapsed ? "center" : "flex-start",
            }}
          >
            <span className={isCollapsed ? "" : "me-2"}>{item.icon}</span>
            {!isCollapsed && <span>{item.label}</span>}
          </span>
        ) : (
          <Link
            href={item.url || "#"}
            className="d-flex align-items-center icon-wrapper w-100 h-100"
            ref={iconRef}
            onClick={onClick}
            style={{
              color:
                isActive || showTooltip
                  ? "var(--bs-bota-light-blue)"
                  : "var(--bs-white)",
              background:
                isActive || showTooltip ? "var(--bs-white)" : "transparent",
              textDecoration: "none",
              paddingLeft: isCollapsed ? "0" : "16px",
              justifyContent: isCollapsed ? "center" : "flex-start",
            }}
          >
            <span className={isCollapsed ? "" : "me-2"}>{item.icon}</span>
            {!isCollapsed && <span>{item.label}</span>}
          </Link>
        )}

        {/* Tooltip - only show when sidebar is collapsed */}
        {shouldShowTooltip &&
          createPortal(
            <div
              className="position-fixed"
              style={{
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                backgroundColor: "white",
                color: "#333",
                padding: "8px 12px",
                borderRadius: "6px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                fontSize: "14px",
                fontWeight: "500",
                whiteSpace: "nowrap",
                // Sidebar is layer 40; shared popups begin at layer 50.
                zIndex: 45,
                border: "1px solid #e0e0e0",
                transform: "translateY(-50%)",
              }}
            >
              {item.label}
              <div
                style={{
                  position: "absolute",
                  left: "-6px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  width: "0",
                  height: "0",
                  borderTop: "6px solid transparent",
                  borderBottom: "6px solid transparent",
                  borderRight: "6px solid white",
                }}
              />
            </div>,
            document.getElementById("tooltip-root")
          )}
      </div>
    );
  }
);

SidebarItem.displayName = "SidebarItem";
export default SidebarItem;
